import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import {
  EventBus,
  FindState,
  PDFFindController,
  PDFLinkService,
  PDFViewer,
} from 'pdfjs-dist/legacy/web/pdf_viewer';

const emptyMatches = { current: 0, total: 0 };
const pdfTextLayerModeEnabled = 1;

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.js',
  import.meta.url
).toString();

const destroyPdfResource = (resource) => {
  try {
    const result = resource?.destroy?.();
    result?.catch?.(() => {});
  } catch {
    // Ignore cleanup errors from interrupted PDF render tasks.
  }
};

const detachPdfViewerDocument = (pdfViewer) => {
  try {
    pdfViewer?.setDocument?.(null);
  } catch {
    // PDF.js can throw while tearing down annotation editors during route/logout unmounts.
  }
};

const SearchablePdfPreview = ({
  onSearchStateChange,
  pdfPreview,
  searchDirection = 'next',
  searchRequestId = 0,
  searchTerm = '',
  text,
}) => {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const eventBusRef = useRef(null);
  const pdfViewerRef = useRef(null);
  const findControllerRef = useRef(null);
  const loadingTaskRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const sourceRef = useRef({ name: 'quizify-pdf-search' });
  const lastSearchRef = useRef({ term: '', requestId: 0 });
  const lastHandledSelectedMatchRef = useRef('');
  const selectedScrollTimersRef = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isReady, setIsReady] = useState(false);

  const clearSelectedScrollTimers = useCallback(() => {
    selectedScrollTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    selectedScrollTimersRef.current = [];
  }, []);

  const scrollSelectedMatchIntoView = useCallback(() => {
    const container = containerRef.current;
    const selectedMatch = container?.querySelector('.textLayer .highlight.selected');

    if (!container || !selectedMatch) {
      return null;
    }

    const containerRect = container.getBoundingClientRect();
    const selectedRect = selectedMatch.getBoundingClientRect();

    if (!selectedRect.width || !selectedRect.height) {
      return null;
    }

    const verticalPadding = Math.min(96, Math.max(36, container.clientHeight * 0.18));
    const horizontalPadding = Math.min(80, Math.max(24, container.clientWidth * 0.12));
    const safeTop = containerRect.top + verticalPadding;
    const safeBottom = containerRect.bottom - verticalPadding;
    const safeLeft = containerRect.left + horizontalPadding;
    const safeRight = containerRect.right - horizontalPadding;
    let targetTop = container.scrollTop;
    let targetLeft = container.scrollLeft;

    if (selectedRect.top < safeTop) {
      targetTop -= safeTop - selectedRect.top;
    } else if (selectedRect.bottom > safeBottom) {
      targetTop += selectedRect.bottom - safeBottom;
    }

    if (selectedRect.left < safeLeft) {
      targetLeft -= safeLeft - selectedRect.left;
    } else if (selectedRect.right > safeRight) {
      targetLeft += selectedRect.right - safeRight;
    }

    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const nextTop = Math.min(Math.max(targetTop, 0), maxTop);
    const nextLeft = Math.min(Math.max(targetLeft, 0), maxLeft);

    if (Math.abs(nextTop - container.scrollTop) < 2 && Math.abs(nextLeft - container.scrollLeft) < 2) {
      return false;
    }

    container.scrollTo({
      top: nextTop,
      left: nextLeft,
      behavior: 'auto',
    });

    return true;
  }, []);

  const scheduleSelectedMatchScroll = useCallback(() => {
    if (!lastSearchRef.current.term) {
      return;
    }

    const selectedMatch = findControllerRef.current?.selected;

    if (!selectedMatch || selectedMatch.pageIdx < 0 || selectedMatch.matchIdx < 0) {
      return;
    }

    const selectedMatchKey = `${lastSearchRef.current.term}:${selectedMatch.pageIdx}:${selectedMatch.matchIdx}`;

    if (lastHandledSelectedMatchRef.current === selectedMatchKey) {
      return;
    }

    clearSelectedScrollTimers();

    selectedScrollTimersRef.current = [0, 90, 220].map((delay) => window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        const scrollResult = scrollSelectedMatchIntoView();

        if (scrollResult !== null) {
          lastHandledSelectedMatchRef.current = selectedMatchKey;
          clearSelectedScrollTimers();
        }
      });
    }, delay));
  }, [clearSelectedScrollTimers, scrollSelectedMatchIntoView]);

  const dispatchFind = useCallback((term, options = {}) => {
    const cleanTerm = String(term || '').trim();
    const eventBus = eventBusRef.current;

    if (!eventBus || !cleanTerm) {
      return;
    }

    eventBus.dispatch('find', {
      source: sourceRef.current,
      type: options.again ? 'again' : '',
      query: cleanTerm,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
      findPrevious: Boolean(options.previous),
      matchDiacritics: false,
    });
  }, []);

  const clearFind = useCallback(() => {
    clearSelectedScrollTimers();
    eventBusRef.current?.dispatch('findbarclose', { source: sourceRef.current });
    lastSearchRef.current = { term: '', requestId: 0 };
    lastHandledSelectedMatchRef.current = '';
    onSearchStateChange?.({
      matches: emptyMatches,
      state: null,
      term: '',
    });
  }, [clearSelectedScrollTimers, onSearchStateChange]);

  useEffect(() => {
    const container = containerRef.current;
    const viewer = viewerRef.current;

    if (!pdfPreview?.blob || !container || !viewer) {
      return undefined;
    }

    let isCancelled = false;
    setIsLoading(true);
    setIsReady(false);
    setLoadError('');
    clearFind();
    viewer.textContent = '';

    const eventBus = new EventBus();
    const linkService = new PDFLinkService({ eventBus });
    const findController = new PDFFindController({ eventBus, linkService });
    const pdfViewer = new PDFViewer({
      container,
      viewer,
      eventBus,
      linkService,
      findController,
      annotationMode: pdfjsLib.AnnotationMode.DISABLE,
      annotationEditorMode: pdfjsLib.AnnotationEditorType.DISABLE,
      textLayerMode: pdfTextLayerModeEnabled,
      removePageBorders: true,
    });

    eventBusRef.current = eventBus;
    pdfViewerRef.current = pdfViewer;
    findControllerRef.current = findController;
    linkService.setViewer(pdfViewer);

    const handlePagesInit = () => {
      pdfViewer.currentScaleValue = 'page-width';
      setIsLoading(false);
      setIsReady(true);
    };
    const handleFindMatchesCount = (event) => {
      onSearchStateChange?.({
        matches: event.matchesCount || emptyMatches,
        state: null,
        term: lastSearchRef.current.term,
      });

      if (event.matchesCount?.current > 0) {
        scheduleSelectedMatchScroll();
      }
    };
    const handleFindState = (event) => {
      onSearchStateChange?.({
        matches: event.matchesCount || emptyMatches,
        state: event.state,
        term: lastSearchRef.current.term,
      });

      if (event.state === FindState.FOUND && event.matchesCount?.current > 0) {
        scheduleSelectedMatchScroll();
      }
    };
    const handleTextLayerMatches = (event) => {
      const selectedMatch = findController.selected;

      if (event.pageIndex !== -1 && event.pageIndex !== selectedMatch.pageIdx) {
        return;
      }

      scheduleSelectedMatchScroll();
    };

    eventBus.on('pagesinit', handlePagesInit);
    eventBus.on('updatefindmatchescount', handleFindMatchesCount);
    eventBus.on('updatefindcontrolstate', handleFindState);
    eventBus.on('updatetextlayermatches', handleTextLayerMatches);

    pdfPreview.blob.arrayBuffer()
      .then((data) => {
        if (isCancelled) {
          return null;
        }

        const loadingTask = pdfjsLib.getDocument({ data });
        loadingTaskRef.current = loadingTask;
        return loadingTask.promise;
      })
      .then((pdfDocument) => {
        if (!pdfDocument || isCancelled) {
          destroyPdfResource(pdfDocument);
          return;
        }

        pdfDocumentRef.current = pdfDocument;
        linkService.setDocument(pdfDocument, null);
        pdfViewer.setDocument(pdfDocument);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        setLoadError(error?.message || text.previewUnavailable);
        setIsLoading(false);
        setIsReady(false);
      });

    return () => {
      isCancelled = true;
      eventBus.off('pagesinit', handlePagesInit);
      eventBus.off('updatefindmatchescount', handleFindMatchesCount);
      eventBus.off('updatefindcontrolstate', handleFindState);
      eventBus.off('updatetextlayermatches', handleTextLayerMatches);
      clearSelectedScrollTimers();
      detachPdfViewerDocument(pdfViewer);
      destroyPdfResource(loadingTaskRef.current);
      destroyPdfResource(pdfDocumentRef.current);
      loadingTaskRef.current = null;
      pdfDocumentRef.current = null;
      eventBusRef.current = null;
      pdfViewerRef.current = null;
      findControllerRef.current = null;
      lastHandledSelectedMatchRef.current = '';
      viewer.textContent = '';
    };
  }, [clearFind, clearSelectedScrollTimers, onSearchStateChange, pdfPreview?.blob, scheduleSelectedMatchScroll, text.previewUnavailable]);

  useEffect(() => {
    const handleResize = () => {
      if (pdfViewerRef.current) {
        pdfViewerRef.current.currentScaleValue = 'page-width';
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const cleanTerm = String(searchTerm || '').trim();

    if (!isReady) {
      return;
    }

    if (!cleanTerm) {
      clearFind();
      return;
    }

    const previousSearch = lastSearchRef.current;
    const isSameTerm = previousSearch.term === cleanTerm;
    const isNewRequest = previousSearch.requestId !== searchRequestId;

    if (!isNewRequest) {
      return;
    }

    lastSearchRef.current = { term: cleanTerm, requestId: searchRequestId };
    onSearchStateChange?.({
      matches: emptyMatches,
      state: FindState.PENDING,
      term: cleanTerm,
    });
    dispatchFind(cleanTerm, {
      again: isSameTerm,
      previous: searchDirection === 'previous',
    });
  }, [clearFind, dispatchFind, isReady, onSearchStateChange, searchDirection, searchRequestId, searchTerm]);

  return (
    <div className="quizify-pdfjs-frame">
      {isLoading && (
        <div className="quizify-pdf-preview-state">
          <Spinner animation="border" size="sm" />
          <span>{text.pdfPreviewLoading}</span>
        </div>
      )}

      {loadError && (
        <Alert variant="danger" className="quizify-pdfjs-error">
          {loadError}
        </Alert>
      )}

      <div ref={containerRef} className="quizify-pdfjs-viewer-container">
        <div ref={viewerRef} className="pdfViewer quizify-pdfjs-viewer" />
      </div>
    </div>
  );
};

export { FindState };
export default SearchablePdfPreview;
